import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import BrandLogo from '../brand/BrandLogo';
import { BRAND_NAME } from '../../constants/brand';
import {
  MAIN_NAV_LINKS,
  CONTACT_PATH,
  HELP_CENTER_PATH,
  getListEventPath,
  getDashboardPath,
  SUPPORT_EMAIL
} from '../../constants/siteNav';
import { isAdminRole, isStaffRole } from '../../utils/roles';

const NAV_ICONS = {
  '/events': 'fa-compass',
  '/categories': 'fa-th-large',
  '/about': 'fa-heart',
  [CONTACT_PATH]: 'fa-headset'
};

const linkClass = (active) =>
  `group flex items-center gap-2.5 px-2.5 py-2 rounded-lg text-[13px] font-medium transition-colors ${
    active
      ? 'bg-orange-50 text-orange-700'
      : 'text-gray-700 hover:bg-gray-50'
  }`;

const iconWrapClass = (active) =>
  `w-7 h-7 rounded-md flex items-center justify-center shrink-0 ${
    active ? 'bg-orange-500 text-white' : 'bg-gray-100 text-gray-500'
  }`;

const MobileDrawer = ({
  open,
  onClose,
  user,
  isAuthenticated,
  isActive,
  onLogout
}) => {
  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="fixed inset-0 z-[600] lg:hidden">
      <div
        className="absolute inset-0 bg-gray-950/55"
        aria-hidden
        onClick={onClose}
      />
      <aside
        className="absolute left-0 top-0 h-full w-[min(100vw,300px)] bg-[#FFFbf7] flex flex-col animate-slide-in-left overflow-hidden"
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
      >
        <div
          className="pointer-events-none absolute inset-x-0 top-0 h-28 opacity-80"
          aria-hidden
          style={{
            background:
              'radial-gradient(ellipse 90% 80% at 10% -20%, rgba(251,146,60,0.18), transparent 55%)'
          }}
        />

        {/* Header */}
        <div className="relative shrink-0 px-3 pt-3.5 pb-1">
          <div className="flex items-center justify-between gap-2">
            <BrandLogo
              size="md"
              onClick={onClose}
              className="min-w-0"
              imgClassName="rounded-md"
            />
            <button
              type="button"
              onClick={onClose}
              className="w-8 h-8 flex items-center justify-center rounded-full bg-white text-gray-500 border border-gray-200 hover:text-gray-800 transition-colors shrink-0"
              aria-label="Close menu"
            >
              <i className="fas fa-times text-xs" />
            </button>
          </div>
          <p className="mt-1.5 text-[10px] font-medium text-gray-500 px-0.5">
            Discover events across Kenya
          </p>
        </div>

        {/* Body */}
        <div className="relative flex-1 min-h-0 px-3 flex flex-col">
          <p className="mt-3 px-0.5 mb-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-gray-400">
            Explore
          </p>
          <nav className="space-y-0.5" aria-label="Main">
            {MAIN_NAV_LINKS.map((link) => {
              const active = isActive(link.to);
              return (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={onClose}
                  className={linkClass(active)}
                >
                  <span className={iconWrapClass(active)}>
                    <i className={`fas ${NAV_ICONS[link.to] || 'fa-circle'} text-[11px]`} />
                  </span>
                  <span className="flex-1">{link.label}</span>
                </Link>
              );
            })}
            <Link
              to={CONTACT_PATH}
              onClick={onClose}
              className={linkClass(isActive(CONTACT_PATH))}
            >
              <span className={iconWrapClass(isActive(CONTACT_PATH))}>
                <i className="fas fa-headset text-[11px]" />
              </span>
              <span className="flex-1">Contact Us</span>
            </Link>
          </nav>

          <Link
            to={getListEventPath(user)}
            onClick={onClose}
            className="mt-2.5 flex items-center justify-center gap-1.5 w-full py-2 rounded-lg border border-orange-200 bg-orange-50/60 text-orange-700 text-[13px] font-medium hover:bg-orange-50 hover:border-orange-300 transition-colors"
          >
            <i className="fas fa-plus text-[10px]" />
            List your event
          </Link>

          {isAuthenticated ? (
            <div className="mt-2.5 rounded-lg bg-white border border-gray-100 overflow-hidden">
              <div className="px-2.5 py-2 border-b border-gray-50">
                <p className="text-[12px] font-semibold text-gray-900 truncate leading-tight">
                  {user?.name}
                </p>
                <p className="text-[10px] text-gray-500 truncate">{user?.email}</p>
              </div>
              <div className="py-0.5">
                <Link
                  to={isStaffRole(user?.role) ? '/dashboard' : getDashboardPath(user)}
                  onClick={onClose}
                  className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50"
                >
                  <i className="fas fa-ticket-alt text-orange-400 w-3.5 text-center text-[10px]" />
                  My tickets
                </Link>
                <Link
                  to="/dashboard"
                  onClick={onClose}
                  className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50"
                >
                  <i className="fas fa-user text-gray-400 w-3.5 text-center text-[10px]" />
                  Account
                </Link>
                {isStaffRole(user?.role) && (
                  <>
                    <Link
                      to="/admin/sales"
                      onClick={onClose}
                      className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50"
                    >
                      <i className="fas fa-chart-line text-gray-400 w-3.5 text-center text-[10px]" />
                      Sales
                    </Link>
                    <Link
                      to="/admin/events"
                      onClick={onClose}
                      className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50"
                    >
                      <i className="fas fa-calendar-alt text-gray-400 w-3.5 text-center text-[10px]" />
                      Events
                    </Link>
                    {isAdminRole(user?.role) && (
                      <Link
                        to="/admin/dashboard"
                        onClick={onClose}
                        className="flex items-center gap-2 px-2.5 py-1.5 text-[12px] text-gray-700 hover:bg-gray-50"
                      >
                        <i className="fas fa-cog text-gray-400 w-3.5 text-center text-[10px]" />
                        Admin
                      </Link>
                    )}
                  </>
                )}
                <button
                  type="button"
                  onClick={() => {
                    onLogout();
                    onClose();
                  }}
                  className="flex w-full items-center gap-2 px-2.5 py-1.5 text-[12px] text-red-600 hover:bg-red-50"
                >
                  <i className="fas fa-sign-out-alt w-3.5 text-center text-[10px]" />
                  Sign out
                </button>
              </div>
            </div>
          ) : null}

          {/* Fills remaining height with useful secondary links - no empty void */}
          <div className="mt-auto pt-4 pb-1">
            <p className="px-0.5 mb-1.5 text-[9px] font-bold uppercase tracking-[0.12em] text-gray-400">
              More
            </p>
            <div className="rounded-lg border border-amber-100/80 bg-white/70 px-2.5 py-1.5">
              <Link
                to={HELP_CENTER_PATH}
                onClick={onClose}
                className="flex items-center gap-2 py-1.5 text-[12px] text-gray-600 hover:text-orange-600"
              >
                <i className="fas fa-life-ring w-3.5 text-center text-[10px] text-gray-400" />
                Help center
              </Link>
              <Link
                to="/terms-of-service"
                onClick={onClose}
                className="flex items-center gap-2 py-1.5 text-[12px] text-gray-600 hover:text-orange-600"
              >
                <i className="fas fa-file-alt w-3.5 text-center text-[10px] text-gray-400" />
                Terms of Service
              </Link>
              <Link
                to="/privacy-policy"
                onClick={onClose}
                className="flex items-center gap-2 py-1.5 text-[12px] text-gray-600 hover:text-orange-600"
              >
                <i className="fas fa-shield-alt w-3.5 text-center text-[10px] text-gray-400" />
                Privacy Policy
              </Link>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative shrink-0 border-t border-amber-100/80 bg-white px-3 py-2.5">
          {isAuthenticated ? (
            <a
              href={`mailto:${SUPPORT_EMAIL}`}
              className="flex items-center justify-center gap-1.5 text-[11px] font-medium text-gray-500 hover:text-orange-600"
            >
              <i className="fas fa-envelope text-[9px]" />
              {SUPPORT_EMAIL}
            </a>
          ) : (
            <div className="flex gap-2">
              <Link
                to="/login"
                onClick={onClose}
                className="flex-1 flex items-center justify-center py-2 text-[12px] font-semibold text-gray-800 bg-white border border-gray-200 rounded-lg hover:bg-gray-50"
              >
                Sign in
              </Link>
              <Link
                to="/register"
                onClick={onClose}
                className="flex-1 flex items-center justify-center py-2 text-[12px] font-semibold text-white bg-orange-500 rounded-lg hover:bg-orange-600"
              >
                Sign up
              </Link>
            </div>
          )}
          <p className="mt-1.5 text-center text-[9px] text-gray-400">
            {BRAND_NAME} · Secure ticketing
          </p>
        </div>
      </aside>
    </div>,
    document.body
  );
};

export default MobileDrawer;
